import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  IconButton,
  Typography,
  Grid,
  Button,
  Paper,
  Slide,
  Dialog,
  DialogContent,
  AppBar,
  Toolbar
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import LoadingButton from '../components/LoadingButton'
import LoadingCircle from '../components/common/LoadingCircle'
import { smallScreenWidth } from '../constants/AppConstants'
import AssetDetail from '../components/AssetDetail'
import { useAm80ReactTableServerSideCols } from '../hooks/AssetCols';
import { DashboardAthenaTable } from '../components/DashboardAthenaTable'
import DateInput from '../components/DateInput'
import OpenSearchDropdown from '../components/OpenSearchDropdown'
import fetchFromAthena from '../functions/FetchFromAthena'

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    margin: '2rem 1rem',
  },
  footerCell: {
    backgroundColor: '#F5F5F5',
    borderBottom: 'none',
    zIndex: 999,
  },
  stickyFooterCell: {
    position: 'sticky',
    bottom: 0,
    zIndex: 100,
  },
  rightTableHead: {
    '& > span': {
      justifyContent: 'flex-end',
      marginRight: '0',
    },
  },
  appBar: {
    position: 'relative',
    backgroundColor: (props) => props.primaryColor,
    height: '60px',
  },
}));

const defaultHeaders = {
  company: '',
  book: '',
  process: '',
  leaseCompany: '',
  lease: '',
  asset: '',
  fromDate: '',
  toDate: '',
};

const TWO_YEARS_MS = 2 * 365.25 * 24 * 60 * 60 * 1000;

/**
 * Validates that the user has provided either an Asset or a Date Range (or both).
 * If only date range is provided (no asset), it must not exceed 2 years.
 * @param {{ asset?: string, fromDate?: string, toDate?: string }} headers
 * @returns {{ valid: boolean, error?: string }}
 */
function validateAssetOrDateRange(headers) {
  const hasAsset = !!headers.asset?.trim?.();
  const hasFromDate = !!headers.fromDate;
  const hasToDate = !!headers.toDate;
  const hasDateRange = hasFromDate && hasToDate;

  if (!hasAsset && !hasDateRange) {
    return { valid: false, error: 'An asset or a date range is required' };
  }

  if (!hasAsset && hasDateRange) {
    const from = new Date(headers.fromDate);
    const to = new Date(headers.toDate);
    const diffMs = Math.abs(to - from);
    if (diffMs > TWO_YEARS_MS) {
      return {
        valid: false,
        error: 'Date range must be less than two years if asset is not specified',
      };
    }
  }

  return { valid: true };
}
export default function TransationAnalysisServerSide(props) {
  const [state, setstate] = useState({
    isLoading: false,
    data: null,
    fetched: true,
    tableKey: JSON.stringify(defaultHeaders),
  });
  const [showTable, setShowTable] = useState(false);
  const [headers, setHeaders] = useState(defaultHeaders);
  const [clearHeader, setClearHeaders] = React.useState(false);
  const [clearTrigger, setClearTrigger] = React.useState(false);
  const [assetData, setAssetData] = useState({
    open: false,
    assetData: null,
    items: [],
    books: [],
  });

  const am80ReactTableServerSideCols = useAm80ReactTableServerSideCols();

  const { credentials } = props.fetchInitialData;
  const classes = useStyles();

  const handleSubmit = (e) => {
    e.preventDefault();
    const validation = validateAssetOrDateRange(headers);
    if (!validation.valid) {
      props.createSnack(validation.error, 'error', 4000);
      return;
    }
    if (JSON.stringify(headers) === state.tableKey) return null;
    setShowTable(true);
    setstate({ ...state, isLoading: true, tableKey: JSON.stringify(headers) });
  };

  const tableDataTransformation = (data) => {
    return data.map((row) => {
      row['ASSET_LINK'] = (
        <div
          className="editLink"
          onClick={() => getAsset(row.COMPANY, row.ASSET)}
        >
          {row.ASSET}
        </div>
      );

      return row;
    });
  };

  const handleCallback = (values, error) => {
    if (error) {
      setstate({ ...state, isLoading: false });
      setShowTable(false);
      return null;
    }
    setstate({ ...state, isLoading: false });
  };

  if (!state.fetched) {
    return <LoadingCircle />;
  }

  const getAsset = async (comp, id) => {
    try {
      if (!id || !comp) return null
      setAssetData({ ...assetData, open: true })
      const queries = [
        'am_asset_read',
        'am_item_read',
        'am_book_read'
      ]
  
      const data = await Promise.allSettled(
        queries.map(query =>
          fetchFromAthena({
            queryName: query,
            headers: {
              company: comp,
              asset: id
            },
            accessToken: credentials.user.accessToken,
            credentials: credentials
          })
        )
      ).then(results => {
        return results
      })

      const [main, items,books] = [data[0].value[0], data[1].value, data[2].value]
      if(!main) {
        props.createSnack('Asset not found', 'warning', 3000)
        setAssetData({ ...state, isLoading: false });
        return
      }

const assetDesc = main.ASSET_DESC
setAssetData({
  ...assetData,
  open: true,
        assetData: main,
        items: items.map(row => {
          return {
            ...row,
            COMPANY: comp,
            ASSET: id,
            ASSET_DESC: assetDesc
          }
        }),
        books: books.map(row => {
          return {
            ...row,
            COMPANY: comp,
            ASSET: id,
            ASSET_DESC: assetDesc
          }
        }),
        isLoading: false
      })

    } catch (e) {
      console.log(e)
    }
  }
  return (
    <div style={{ padding: '1.5rem 0.5rem' }}>
      <Dialog
        open={assetData.open}
        TransitionComponent={Transition}
        maxWidth="xl"
        fullWidth
        fullScreen={credentials.appWidth < smallScreenWidth ? true : false}
        scroll="body"
        keepMounted
      >
        <AppBar className={classes.appBar}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() =>
                setAssetData({
                  ...assetData,
                  assetData: null,
                  items: [],
                  books: [],
                  open: false,
                })
              }
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        {assetData.open && (
          <DialogContent style={{ padding: 0 }}>
            {!!assetData.assetData ? (
              <AssetDetail
                {...props}
                assetData={assetData.assetData}
                items={assetData.items}
                books={assetData.books}
              />
            ) : (
              <LoadingCircle />
            )}
          </DialogContent>
        )}
      </Dialog>

      <IconButton
        style={{ borderRadius: '5px', position: 'absolute' }}
        size="small"
        onClick={() => props.history.push('/')}
        aria-label="delete"
      >
        <ArrowBackIcon />
        {props.fetchInitialData.credentials.appWidth > smallScreenWidth &&
          'Dashboard'}
      </IconButton>
      <Typography variant="h5" style={{ textAlign: 'center' }}>
        Transaction Analysis (AM80.1)
      </Typography>

      <div className={classes.root}>
        <Paper elevation={0} style={{ padding: '1rem' }}>
          <form onSubmit={handleSubmit}>
            <Grid
              container
              alignContent="center"
              alignItems="flex-start"
              spacing={3}
            >
              <Grid item>
                <OpenSearchDropdown
                  id='company'
                  required
                  width='250px'
                  label='Company'
                  page="EMPLOYEE"
                  entity="COMPANY"
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  onChange={(val) => {
                    setHeaders({ ...headers, company: val?.VALUE || '' })
                  }}
                />
              </Grid>
              <Grid item>
                <OpenSearchDropdown
                  id='book'
                  required
                  width='200px'
                  label='Book'
                  page="TRANSACTIONANALYSIS"
                  entity="BOOK"
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  onChange={(val) => {
                    setHeaders({ ...headers, book: val?.VALUE || '' })
                  }}
                />
              </Grid>

              <Grid item>
                <OpenSearchDropdown
                  id='asset'
                  required={false}
                  width='175px'
                  label='Asset'
                  page="ASSET"
                  entity="ASSET"
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  onChange={(val) => {
                    setHeaders({ ...headers, asset: val?.VALUE || '' })
                  }}
                />
              </Grid>

              <Grid item>
                <OpenSearchDropdown
                  id='process'
                  required={false}
                  width='175px'
                  label='Process'
                  page="TRANSACTIONANALYSIS"
                  entity="PROCESS"
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  onChange={(val) => {
                    setHeaders({ ...headers, process: val?.VALUE || '' })
                  }}
                />
              </Grid>
              {/*<Grid item>*/}
              {/*  <OpenSearchDropdown*/}
              {/*    id='leaseCompany'*/}
              {/*    required={false}*/}
              {/*    width='175px'*/}
              {/*    label='Lease Company'*/}
              {/*    page="ASSETANALYSIS"*/}
              {/*    entity="LEASECOMPANY"*/}
              {/*    searchFields={['DISPLAY']}*/}
              {/*    accessToken={credentials.user.accessToken}*/}
              {/*    credentials={credentials}*/}
              {/*    clearTrigger={clearTrigger}*/}
              {/*    onChange={(val) => {*/}
              {/*      setHeaders({ ...headers, leaseCompany: val?.VALUE || '' })*/}
              {/*    }}*/}
              {/*  />*/}
              {/*</Grid>*/}
              {/*<Grid item>*/}
              {/*  <OpenSearchDropdown*/}
              {/*    id='lease'*/}
              {/*    required={false}*/}
              {/*    width='175px'*/}
              {/*    label='Lease'*/}
              {/*    page="ASSETANALYSIS"*/}
              {/*    entity="LEASE"*/}
              {/*    searchFields={['DISPLAY']}*/}
              {/*    accessToken={credentials.user.accessToken}*/}
              {/*    credentials={credentials}*/}
              {/*    clearTrigger={clearTrigger}*/}
              {/*    onChange={(val) => {*/}
              {/*      setHeaders({ ...headers, lease: val?.VALUE || '' })*/}
              {/*    }}*/}
              {/*  />*/}
              {/*</Grid>*/}

              {[
                {
                  id: 'fromDate',
                  label: 'From Date',
                },
                {
                  id: 'toDate',
                  label: 'To Date',
                },
              ].map((field) => {
                return (
                  <Grid key={field.id} item>
                    <DateInput
                      id={field.id}
                      key={`${field.id}-${clearHeader}`}
                      label={field.label}
                      value={headers[field.id] || ''}
                      onChange={(e) => {
                        setHeaders({
                          ...headers,
                          [field.id]: !!e.target.value ? e.target.value : null,
                        });
                      }}
                      required={false}
                      variant="standard"
                    />
                  </Grid>
                );
              })}
            </Grid>
            <div style={{ marginTop: '1rem' }}>
              <LoadingButton
                label="Submit"
                isLoading={state.isLoading}
                color="primaryVLButton"
                buttonType="submit"
              />
              {!!Object.values(headers).some(value => value !== '' && value !== null) && (
                <Button
                  onClick={() => {
                    setClearHeaders(!clearHeader)
                    setHeaders(defaultHeaders)
                    setClearTrigger(prev => !prev)
                  }}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Clear
                </Button>
              )}
            </div>
          </form>
        </Paper>
        {showTable && (
          <div style={{ marginTop: '1rem ' }}>
            <DashboardAthenaTable
              {...props}
              isTempTable={true}
              key={state.tableKey}
              params={{ ...headers }}
              tableColumns={am80ReactTableServerSideCols}
              reportParamName="am_am80_read_serverside_v3"
              reportName='am_am80_read_serverside_v3_temp'
              tableTitle={''}
              callback={handleCallback}
              transform={tableDataTransformation}
              downloadFileName={'Transaction_Analysis_Download'}
            />
          </div>
        )}
      </div>
    </div>
  );
}
